import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminAuth from '../components/admin/AdminAuth';
import LeadCard from '../components/admin/LeadCard';
import {
  getLeads,
  getOutreachEmails,
  getSentEmails,
  updateOutreachEmail,
  deleteOutreachEmail,
  createOutreachEmail,
  callSendOutreachEmail,
  getFollowUpQueue,
  callFetchInboxEmails,
  getInboxEmails,
  callRespinOutreachEmail
} from '../services/contentOpsService';

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EMAIL_STATUS_COLORS = {
  draft: 'bg-yellow-600/20 text-yellow-400',
  approved: 'bg-green-600/20 text-green-400',
  sent: 'bg-blue-600/20 text-blue-400'
};

const OutreachPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [emails, setEmails] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [editingSubject, setEditingSubject] = useState('');
  const [sending, setSending] = useState(null);
  const [activeSection, setActiveSection] = useState('drafts');
  const [inboxEmails, setInboxEmails] = useState([]);
  const [fetchingInbox, setFetchingInbox] = useState(false);
  const [expandedInboxId, setExpandedInboxId] = useState(null);
  const [inboxFilter, setInboxFilter] = useState('all');
  const [feedback, setFeedback] = useState('');
  const [respinning, setRespinning] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [bccNext, setBccNext] = useState(false);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '', leadId: '' });
  const [savingCompose, setSavingCompose] = useState(false);
  const [editingToId, setEditingToId] = useState(null);
  const [editingToValue, setEditingToValue] = useState('');

  useEffect(() => { loadData(); }, []);

  // Auto-expand the first draft email on initial load only
  const hasAutoExpanded = React.useRef(false);
  useEffect(() => {
    if (hasAutoExpanded.current) return;
    const firstDraft = emails.find(e => e.status === 'draft' || e.status === 'approved');
    if (firstDraft && activeSection === 'drafts') {
      setExpandedEmail(firstDraft.id);
      setEditingBody(firstDraft.body || '');
      hasAutoExpanded.current = true;
    }
  }, [emails]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [l, e, sent, f] = await Promise.all([
        getLeads(null, 100),
        getOutreachEmails(null, 200),
        getSentEmails(500),
        getFollowUpQueue().catch(() => [])
      ]);
      // Merge sent emails in — dedup by id in case any overlap with the active pool
      const sentIds = new Set(sent.map(s => s.id));
      const merged = [...e.filter(em => !sentIds.has(em.id)), ...sent];
      const inbox = await getInboxEmails(100).catch(() => []);
      setLeads(l);
      setEmails(merged);
      setFollowUps(f);
      setInboxEmails(inbox);
    } catch (err) {
      console.error('OutreachPage load error:', err);
      setError('Failed to load outreach data');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchInbox = async () => {
    try {
      setFetchingInbox(true);
      setError('');
      await callFetchInboxEmails(50);
      const inbox = await getInboxEmails(100);
      setInboxEmails(inbox);
    } catch (err) {
      console.error('Fetch inbox error:', err);
      setError('Failed to fetch inbox. Check SMTP secrets are configured.');
    } finally {
      setFetchingInbox(false);
    }
  };

  const handleApproveEmail = async (emailId) => {
    try {
      await updateOutreachEmail(emailId, { status: 'approved', approvedAt: new Date() });
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, status: 'approved' } : e));
    } catch (err) {
      setError('Failed to approve email');
    }
  };

  const handleSaveEdit = async (emailId) => {
    try {
      await updateOutreachEmail(emailId, { subject: editingSubject, body: editingBody });
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, subject: editingSubject, body: editingBody } : e));
      setSavedId(emailId);
      setTimeout(() => setSavedId(null), 1000);
    } catch (err) {
      setError('Failed to save edit');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Delete this email draft permanently?')) return;
    try {
      await deleteOutreachEmail(emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
    } catch (err) {
      console.error('Delete email error:', err);
      setError('Failed to delete email');
    }
  };

  const handleSendEmail = async (emailId) => {
    try {
      setSending(emailId);
      setSendError(null);
      setError('');
      await callSendOutreachEmail(emailId, bccNext ? { bcc: 'christopher@indiechess.com' } : {});
      setBccNext(false);
      // Reload from Firestore to get sentBody + sentTo written by the Cloud Function
      const [refreshed, refreshedSent] = await Promise.all([
        getOutreachEmails(null, 200),
        getSentEmails(500)
      ]);
      const refreshedSentIds = new Set(refreshedSent.map(s => s.id));
      setEmails([...refreshed.filter(em => !refreshedSentIds.has(em.id)), ...refreshedSent]);
    } catch (err) {
      console.error('Send email error:', err);
      const msg = err.message || 'Failed to send email';
      setError(msg);
      setSendError({ emailId, msg });
    } finally {
      setSending(null);
    }
  };

  const handleRespin = async (emailId) => {
    if (!feedback.trim()) return;
    try {
      setRespinning(emailId);
      setError('');
      const result = await callRespinOutreachEmail(emailId, editingBody, feedback.trim());
      setEditingBody(result.body);
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, body: result.body, version: result.version } : e));
      setFeedback('');
    } catch (err) {
      console.error('Respin error:', err);
      setError(err.message || 'Failed to respin email');
    } finally {
      setRespinning(null);
    }
  };

  const toggleExpand = (emailId, body, subject) => {
    if (expandedEmail === emailId) {
      setExpandedEmail(null);
      setEditingBody('');
      setEditingSubject('');
    } else {
      setExpandedEmail(emailId);
      setEditingBody(body || '');
      setEditingSubject(subject || '');
    }
  };

  const getLeadForEmail = (leadId) => leads.find(l => l.id === leadId);

  const handleCopyBuildWebsite = (email) => {
    const lead = getLeadForEmail(email.leadId);
    const formatLinks = (l) => {
      if (!l?.links || Object.keys(l.links).length === 0) return 'none';
      return Object.entries(l.links).map(([k, v]) => `${k}: ${v}`).join(', ');
    };

    const text = `Run /create-profile for this outreach lead, then update the email draft.

Lead ID: ${email.leadId}
Name: ${lead?.name || lead?.widgetTitle || 'unknown'}
Role: ${lead?.role || 'unknown'}
Email: ${lead?.contactEmail || 'none'}
Website: ${lead?.link || 'none'}
Social links: ${formatLinks(lead)}
Lead score: ${lead?.leadScore ?? 'unknown'}
Suggested angle: ${lead?.suggestedAngle || 'none'}
Notes: ${lead?.notes || 'none'}

Current email draft (ID: ${email.id}):
Subject: ${email.subject || ''}
Body:
${email.body || ''}

After creating and publishing the profile:
1. Update lead doc "${email.leadId}" in outreach_leads (project: indie-chess) with profileId, profileUrl, claimUrl, profileCreatedAt
2. Rewrite email draft "${email.id}" in outreach_emails — naturally weave in the profile URL, mention they can connect their own .com domain. Keep the same tone and approach, just add the website reference.`;

    navigator.clipboard.writeText(text);
    setCopiedEmailId(email.id);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  const handleApproveAll = async () => {
    const drafts = emails.filter(e => e.status === 'draft');
    if (drafts.length === 0) return;
    if (!window.confirm(`Approve all ${drafts.length} draft emails?`)) return;
    setBulkApproving(true);
    setBulkProgress({ done: 0, total: drafts.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < drafts.length; i++) {
      try {
        await updateOutreachEmail(drafts[i].id, { status: 'approved', approvedAt: new Date() });
        setEmails(prev => prev.map(e => e.id === drafts[i].id ? { ...e, status: 'approved' } : e));
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: drafts.length, failed });
    }
    setBulkApproving(false);
  };

  const handleSendAll = async () => {
    const approved = emails.filter(e => e.status === 'approved');
    const sendable = approved.filter(e => {
      const lead = getLeadForEmail(e.leadId);
      return lead?.contactEmail || e.recipientEmail;
    });
    if (sendable.length === 0) return;
    if (!window.confirm(`Send ${sendable.length} approved emails now? ${approved.length - sendable.length > 0 ? `(${approved.length - sendable.length} skipped — no email on lead)` : ''}`)) return;
    setBulkSending(true);
    setBulkProgress({ done: 0, total: sendable.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < sendable.length; i++) {
      try {
        await callSendOutreachEmail(sendable[i].id, bccNext && i === 0 ? { bcc: 'christopher@indiechess.com' } : {});
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: sendable.length, failed });
    }
    setBccNext(false);
    // Refresh all email data
    try {
      const [refreshed, refreshedSent] = await Promise.all([
        getOutreachEmails(null, 200),
        getSentEmails(500)
      ]);
      const refreshedSentIds = new Set(refreshedSent.map(s => s.id));
      setEmails([...refreshed.filter(em => !refreshedSentIds.has(em.id)), ...refreshedSent]);
    } catch {}
    setBulkSending(false);
  };

  const handleDeleteAllDrafts = async () => {
    if (!confirmDeleteAll) {
      setConfirmDeleteAll(true);
      setTimeout(() => setConfirmDeleteAll(false), 5000);
      return;
    }
    const drafts = emails.filter(e => e.status === 'draft' || e.status === 'approved');
    if (drafts.length === 0) return;
    setBulkDeleting(true);
    setConfirmDeleteAll(false);
    setBulkProgress({ done: 0, total: drafts.length, failed: 0 });
    let failed = 0;
    for (let i = 0; i < drafts.length; i++) {
      try {
        await deleteOutreachEmail(drafts[i].id);
      } catch {
        failed++;
      }
      setBulkProgress({ done: i + 1, total: drafts.length, failed });
    }
    setEmails(prev => prev.filter(e => e.status !== 'draft' && e.status !== 'approved'));
    setBulkDeleting(false);
  };

  const handleSaveTo = async (emailId) => {
    try {
      await updateOutreachEmail(emailId, { recipientEmail: editingToValue.trim() });
      setEmails(prev => prev.map(e => e.id === emailId ? { ...e, recipientEmail: editingToValue.trim() } : e));
      setEditingToId(null);
    } catch (err) {
      setError('Failed to save recipient');
    }
  };

  const handleCreateEmail = async () => {
    if (!composeData.to && !composeData.subject && !composeData.body) return;
    setSavingCompose(true);
    try {
      const lead = composeData.leadId ? leads.find(l => l.id === composeData.leadId) : null;
      await createOutreachEmail({
        to: composeData.to,
        subject: composeData.subject,
        body: composeData.body,
        leadId: composeData.leadId || null,
        leadName: lead?.name || lead?.widgetTitle || ''
      });
      setComposing(false);
      setComposeData({ to: '', subject: '', body: '', leadId: '' });
      await loadData();
    } catch (err) {
      console.error('Create email error:', err);
      setError('Failed to create email');
    } finally {
      setSavingCompose(false);
    }
  };

  const draftEmails = emails.filter(e => e.status === 'draft' || e.status === 'approved');
  const sentEmails = emails.filter(e => e.status === 'sent');

  const sections = [
    { key: 'drafts', label: `Drafts (${draftEmails.length})` },
    { key: 'sent', label: `Sent (${sentEmails.length})` },
    { key: 'followups', label: `Follow-ups (${followUps.length})` },
    { key: 'leads', label: `All Leads (${leads.length})` },
    { key: 'inbox', label: `Inbox (${inboxEmails.length})` }
  ];

  return (
    <AdminAuth>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-400 hover:text-gray-200 transition-colors text-sm"
              >
                &larr;
              </button>
              <h1 className="text-3xl font-bold text-gray-100">Outreach CRM</h1>
            </div>
            <p className="text-gray-400 mt-1">Leads, email drafts, and send queue</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* BCC Toggle + Section Tabs */}
        {bccNext && (
          <div className="bg-yellow-900/30 border border-yellow-700/50 text-yellow-400 px-4 py-2 rounded-lg text-xs flex items-center justify-between">
            <span>BCC to christopher@indiechess.com is ON for the next send. It will auto-clear after one email.</span>
            <button onClick={() => setBccNext(false)} className="text-yellow-300 hover:text-yellow-100 ml-4 underline">Cancel</button>
          </div>
        )}
        <div className="flex gap-1 bg-gray-800 rounded-lg p-1 overflow-x-auto">
          {sections.filter(s => s.key !== 'inbox').map(s => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeSection === s.key
                  ? 'bg-gray-700 text-gray-100'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
              }`}
            >
              {s.label}
            </button>
          ))}
          {/* Inbox pushed to far right */}
          <button
            onClick={() => setActiveSection('inbox')}
            className={`ml-auto px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeSection === 'inbox'
                ? 'bg-gray-700 text-gray-100'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
            }`}
          >
            {sections.find(s => s.key === 'inbox')?.label}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
          </div>
        ) : (
          <>
            {/* Leads Section */}
            {activeSection === 'leads' && (
              <div className="space-y-3">
                {leads.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No leads yet. Run <code className="text-yellow-400">/outreach-research</code> to discover and import leads.
                  </p>
                ) : (
                  leads.map(lead => (
                    <LeadCard key={lead.id} lead={lead} onUpdate={loadData} />
                  ))
                )}
              </div>
            )}

            {/* Email Sections */}
            {(activeSection === 'drafts' || activeSection === 'sent') && (
              <div className="space-y-3">
                {/* Bulk action bar for Drafts */}
                {activeSection === 'drafts' && draftEmails.length > 0 && (
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>{emails.filter(e => e.status === 'draft').length} draft</span>
                      <span className="text-gray-600">|</span>
                      <span>{emails.filter(e => e.status === 'approved').length} approved</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {(bulkApproving || bulkSending || bulkDeleting) && (
                        <span className="text-xs text-gray-400">
                          {bulkProgress.done}/{bulkProgress.total}
                          {bulkProgress.failed > 0 && <span className="text-red-400 ml-1">({bulkProgress.failed} failed)</span>}
                        </span>
                      )}
                      <button
                        onClick={handleApproveAll}
                        disabled={bulkApproving || bulkSending || bulkDeleting || emails.filter(e => e.status === 'draft').length === 0}
                        className="bg-green-600/20 hover:bg-green-600/40 disabled:bg-gray-700 disabled:text-gray-500 text-green-400 px-4 py-1.5 rounded text-xs font-medium transition-colors"
                      >
                        {bulkApproving ? `Approving ${bulkProgress.done}/${bulkProgress.total}...` : 'Approve All'}
                      </button>
                      <button
                        onClick={handleSendAll}
                        disabled={bulkApproving || bulkSending || bulkDeleting || emails.filter(e => e.status === 'approved').length === 0}
                        className="bg-blue-600/20 hover:bg-blue-600/40 disabled:bg-gray-700 disabled:text-gray-500 text-blue-400 px-4 py-1.5 rounded text-xs font-medium transition-colors"
                      >
                        {bulkSending ? `Sending ${bulkProgress.done}/${bulkProgress.total}...` : 'Send All Approved'}
                      </button>
                      <button
                        onClick={handleDeleteAllDrafts}
                        disabled={bulkApproving || bulkSending || bulkDeleting || emails.filter(e => e.status === 'draft' || e.status === 'approved').length === 0}
                        className={`${confirmDeleteAll ? 'bg-red-600/50 hover:bg-red-600/70 ring-1 ring-red-500' : 'bg-red-600/20 hover:bg-red-600/40'} disabled:bg-gray-700 disabled:text-gray-500 text-red-400 px-4 py-1.5 rounded text-xs font-medium transition-colors`}
                      >
                        {bulkDeleting ? `Deleting ${bulkProgress.done}/${bulkProgress.total}...` : confirmDeleteAll ? 'Click Again to Confirm' : 'Delete All Drafts'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Compose new email */}
                {activeSection === 'drafts' && (
                  composing ? (
                    <div className="bg-gray-800 border border-blue-700/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-gray-200">Compose Email</h4>
                        <button onClick={() => setComposing(false)} className="text-gray-500 hover:text-gray-300 text-xs">Cancel</button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">To (email)</label>
                          <input
                            type="email"
                            value={composeData.to}
                            onChange={e => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                            placeholder="recipient@example.com"
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Link to Lead (optional)</label>
                          <select
                            value={composeData.leadId}
                            onChange={e => {
                              const lid = e.target.value;
                              const lead = leads.find(l => l.id === lid);
                              setComposeData(prev => ({
                                ...prev,
                                leadId: lid,
                                to: lead?.contactEmail || prev.to
                              }));
                            }}
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">No lead</option>
                            {leads.map(l => (
                              <option key={l.id} value={l.id}>
                                {l.name || l.widgetTitle} {l.contactEmail ? `(${l.contactEmail})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Subject</label>
                        <input
                          type="text"
                          value={composeData.subject}
                          onChange={e => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                          className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Body</label>
                        <textarea
                          rows={10}
                          value={composeData.body}
                          onChange={e => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                          className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm text-gray-200 font-mono focus:border-blue-500 focus:outline-none resize-y"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setComposing(false)}
                          className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-1.5 rounded text-xs transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateEmail}
                          disabled={savingCompose || (!composeData.to && !composeData.subject)}
                          className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:text-gray-500 text-white px-4 py-1.5 rounded text-xs font-medium transition-colors"
                        >
                          {savingCompose ? 'Creating...' : 'Create Draft'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setComposing(true)}
                      className="w-full bg-gray-800 border border-dashed border-gray-600 hover:border-blue-500 hover:bg-gray-800/80 text-gray-400 hover:text-blue-400 rounded-lg py-3 text-sm transition-colors"
                    >
                      + Compose Email
                    </button>
                  )
                )}

                {(activeSection === 'drafts' ? draftEmails : sentEmails
                ).length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No {activeSection === 'drafts' ? 'draft' : 'sent'} emails.
                    {activeSection === 'drafts' && <> Run <code className="text-yellow-400">/outreach-research</code> to draft emails for your leads.</>}
                  </p>
                ) : (
                  (activeSection === 'drafts' ? draftEmails : sentEmails
                  ).map(email => {
                    const lead = getLeadForEmail(email.leadId);
                    return (
                      <div key={email.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded ${EMAIL_STATUS_COLORS[email.status] || 'bg-gray-700 text-gray-400'}`}>
                                {email.status}
                              </span>
                              <span className="text-xs text-gray-500">{email.template}</span>
                              {email.leadScore && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  email.leadScore >= 7 ? 'bg-green-600/20 text-green-400' :
                                  email.leadScore >= 4 ? 'bg-yellow-600/20 text-yellow-400' :
                                  'bg-gray-700 text-gray-400'
                                }`}>
                                  Score: {email.leadScore}
                                </span>
                              )}
                              {email.useCase && (
                                <span className="text-xs text-gray-500">{email.useCase.replace('_', ' ')}</span>
                              )}
                            </div>
                            <h4 className="text-gray-200 font-medium text-sm">
                              Subject: {email.subject || '(no subject)'}
                            </h4>
                            {email.status !== 'sent' && editingToId === email.id ? (
                              <div className="flex items-center gap-1.5 mt-0.5" onClick={e => e.stopPropagation()}>
                                <span className="text-gray-400 text-xs">To:</span>
                                <input
                                  type="email"
                                  value={editingToValue}
                                  onChange={e => setEditingToValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveTo(email.id); if (e.key === 'Escape') setEditingToId(null); }}
                                  autoFocus
                                  placeholder="recipient@example.com"
                                  className="bg-gray-900 border border-blue-500 rounded px-2 py-0.5 text-xs text-gray-200 focus:outline-none w-64"
                                />
                                <button onClick={() => handleSaveTo(email.id)} className="text-blue-400 hover:text-blue-300 text-xs font-medium">Save</button>
                                <button onClick={() => setEditingToId(null)} className="text-gray-500 hover:text-gray-300 text-xs">Cancel</button>
                              </div>
                            ) : (
                              <p
                                className={`text-xs mt-0.5 ${email.status !== 'sent' ? 'cursor-pointer hover:text-blue-400 group' : 'text-gray-400'}`}
                                onClick={email.status !== 'sent' ? (e) => {
                                  e.stopPropagation();
                                  const currentTo = lead?.contactEmail || email.recipientEmail || '';
                                  setEditingToValue(currentTo);
                                  setEditingToId(email.id);
                                } : undefined}
                              >
                                <span className="text-gray-400">To: </span>
                                {(() => {
                                  const recipient = lead?.contactEmail || email.recipientEmail;
                                  const name = lead?.name || lead?.widgetTitle || email.leadName;
                                  if (recipient) {
                                    return <span className="text-gray-400">{name ? `${name} ` : ''}&lt;{recipient}&gt;</span>;
                                  }
                                  return <span className="text-gray-500">(no recipient)</span>;
                                })()}
                                {email.status !== 'sent' && <span className="text-gray-600 group-hover:text-blue-400 ml-1 text-[10px]">edit</span>}
                              </p>
                            )}
                            <span className="text-xs text-gray-500 mt-1 inline-block">{formatDate(email.createdAt)}</span>
                          </div>

                          <div className="flex gap-2 shrink-0 items-center">
                            {lead?.link && (
                              <a
                                href={lead.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:text-blue-300 text-xs truncate max-w-[160px]"
                                title={lead.link}
                              >
                                {lead.link.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                              </a>
                            )}
                            {lead?.profileUrl && (
                              <a
                                href={lead.profileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-400 hover:text-emerald-300 text-xs"
                                title="ChessWeb profile"
                              >
                                CW
                              </a>
                            )}
                            <button
                              onClick={() => toggleExpand(email.id, email.body, email.subject)}
                              className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 rounded text-xs transition-colors"
                            >
                              {expandedEmail === email.id ? 'Close' : 'View'}
                            </button>
                            {email.status === 'draft' && (
                              <button
                                onClick={() => handleApproveEmail(email.id)}
                                className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1 rounded text-xs transition-colors"
                              >
                                Approve
                              </button>
                            )}
                            {email.status === 'approved' && (
                              !getLeadForEmail(email.leadId)?.contactEmail && !email.recipientEmail ? (
                                <span className="bg-gray-700 text-gray-500 px-3 py-1 rounded text-xs cursor-not-allowed">
                                  No email on lead
                                </span>
                              ) : (
                                <>
                                  {!bccNext && (
                                    <button
                                      onClick={() => setBccNext(true)}
                                      className="bg-gray-700 hover:bg-gray-600 text-gray-400 px-2 py-1 rounded text-xs transition-colors"
                                      title="BCC christopher@indiechess.com on this send"
                                    >
                                      BCC me
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleSendEmail(email.id)}
                                    disabled={sending === email.id}
                                    className="bg-green-600/20 hover:bg-green-600/40 disabled:bg-gray-600 disabled:text-gray-500 text-green-400 px-3 py-1 rounded text-xs transition-colors"
                                  >
                                    {sending === email.id ? 'Sending...' : (bccNext ? 'Send + BCC' : 'Send')}
                                  </button>
                                </>
                              )
                            )}
                            {email.status !== 'sent' && (
                              <button
                                onClick={() => handleDeleteEmail(email.id)}
                                className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1 rounded text-xs transition-colors"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>

                        {sendError?.emailId === email.id && (
                          <div className="mt-2 bg-red-900/50 border border-red-700 text-red-300 px-3 py-2 rounded text-xs">
                            Send failed: {sendError.msg}
                          </div>
                        )}

                        {expandedEmail === email.id && (
                          <div className="mt-4 border-t border-gray-700 pt-4">
                            {email.status === 'sent' && email.sentBody ? (
                              <div className="whitespace-pre-wrap font-mono text-sm text-gray-300 bg-gray-900 rounded-lg px-4 py-3 border border-gray-600">
                                {email.sentBody}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                                  <input
                                    type="text"
                                    value={editingSubject}
                                    onChange={(e) => setEditingSubject(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-200 text-sm focus:outline-none focus:border-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Body</label>
                                  <textarea
                                    rows="8"
                                    value={editingBody}
                                    onChange={(e) => setEditingBody(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-gray-200 text-sm font-mono focus:outline-none focus:border-blue-500"
                                  />
                                </div>
                              </div>
                            )}
                            <div className="flex justify-end gap-2 mt-2">
                              {!lead?.profileId && (
                                <button
                                  onClick={() => handleCopyBuildWebsite(email)}
                                  className="bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 px-4 py-1 rounded text-sm transition-colors"
                                >
                                  {copiedEmailId === email.id ? 'Copied!' : 'Build Website'}
                                </button>
                              )}
                              <button
                                onClick={() => handleSaveEdit(email.id)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded text-sm transition-colors"
                              >
                                {savedId === email.id ? 'Saved' : 'Save Edit'}
                              </button>
                            </div>
                            <div className="mt-3 flex gap-2 items-center">
                              <input
                                type="text"
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !respinning && feedback.trim() && handleRespin(email.id)}
                                placeholder="e.g. Make it shorter, mention their coaching program..."
                                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-200 text-sm focus:outline-none focus:border-purple-500 placeholder-gray-500"
                              />
                              <button
                                onClick={() => handleRespin(email.id)}
                                disabled={respinning === email.id || !feedback.trim()}
                                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
                              >
                                {respinning === email.id ? 'Respinning...' : 'Respin'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Inbox Section */}
            {activeSection === 'inbox' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">
                    Emails fetched from christopher@indiechess.com via IMAP.
                  </p>
                  <button
                    onClick={handleFetchInbox}
                    disabled={fetchingInbox}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors"
                  >
                    {fetchingInbox ? 'Fetching...' : 'Fetch New Emails'}
                  </button>
                </div>

                {/* Category filter bar */}
                {(() => {
                  const categoryCounts = {};
                  inboxEmails.forEach(m => {
                    const cat = m.category || 'uncategorized';
                    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                  });
                  const filters = [
                    { key: 'all', label: 'All', color: 'gray' },
                    { key: 'reply_positive', label: 'Interested', color: 'green' },
                    { key: 'inquiry', label: 'Inquiry', color: 'blue' },
                    { key: 'reply_neutral', label: 'Neutral', color: 'yellow' },
                    { key: 'reply_negative', label: 'Not Interested', color: 'red' },
                    { key: 'automated', label: 'Automated', color: 'gray' },
                    { key: 'uncategorized', label: 'Uncategorized', color: 'gray' },
                  ];
                  return (
                    <div className="flex gap-1.5 flex-wrap">
                      {filters.map(f => {
                        const count = f.key === 'all' ? inboxEmails.length : (categoryCounts[f.key] || 0);
                        if (f.key !== 'all' && count === 0) return null;
                        const active = inboxFilter === f.key;
                        const colorMap = {
                          green: active ? 'bg-green-600 text-white' : 'bg-green-900/30 text-green-400 hover:bg-green-900/50',
                          blue: active ? 'bg-blue-600 text-white' : 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50',
                          yellow: active ? 'bg-yellow-600 text-white' : 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50',
                          red: active ? 'bg-red-600 text-white' : 'bg-red-900/30 text-red-400 hover:bg-red-900/50',
                          gray: active ? 'bg-gray-600 text-white' : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700',
                        };
                        return (
                          <button
                            key={f.key}
                            onClick={() => setInboxFilter(f.key)}
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${colorMap[f.color]}`}
                          >
                            {f.label} ({count})
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {inboxEmails.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No emails in the inbox yet. Click "Fetch New Emails" to pull from IMAP.
                  </p>
                ) : (
                  inboxEmails
                    .filter(msg => {
                      if (inboxFilter === 'all') return true;
                      if (inboxFilter === 'uncategorized') return !msg.category;
                      return msg.category === inboxFilter;
                    })
                    .sort((a, b) => {
                      // Sort: reply_positive first, then inquiry, then neutral, then rest
                      const priority = { reply_positive: 0, inquiry: 1, reply_neutral: 2, reply_negative: 3, bounce: 4, ooo: 4, spam: 5, automated: 6 };
                      const pa = priority[a.category] ?? 3;
                      const pb = priority[b.category] ?? 3;
                      if (pa !== pb) return pa - pb;
                      // Within same priority, newest first
                      const da = a.date?.toDate ? a.date.toDate() : (a.date ? new Date(a.date) : new Date(0));
                      const db2 = b.date?.toDate ? b.date.toDate() : (b.date ? new Date(b.date) : new Date(0));
                      return db2 - da;
                    })
                    .map(msg => {
                    const isExpanded = expandedInboxId === msg.id;
                    const msgDate = msg.date?.toDate ? msg.date.toDate() : (msg.date ? new Date(msg.date) : null);
                    const categoryBadge = {
                      reply_positive: { label: 'Interested', cls: 'bg-green-900/40 text-green-400 border-green-700/50' },
                      reply_negative: { label: 'Not Interested', cls: 'bg-red-900/40 text-red-400 border-red-700/50' },
                      reply_neutral: { label: 'Neutral Reply', cls: 'bg-yellow-900/40 text-yellow-400 border-yellow-700/50' },
                      inquiry: { label: 'Inquiry', cls: 'bg-blue-900/40 text-blue-400 border-blue-700/50' },
                      bounce: { label: 'Bounce', cls: 'bg-gray-800 text-gray-500 border-gray-700' },
                      ooo: { label: 'Out of Office', cls: 'bg-gray-800 text-gray-500 border-gray-700' },
                      spam: { label: 'Spam', cls: 'bg-gray-800 text-gray-500 border-gray-700' },
                      automated: { label: 'Automated', cls: 'bg-gray-800 text-gray-500 border-gray-700' },
                    }[msg.category];
                    const borderColor = msg.category === 'reply_positive' ? 'border-green-700/50' :
                      msg.category === 'inquiry' ? 'border-blue-700/50' : 'border-gray-700';
                    return (
                      <div key={msg.id} className={`bg-gray-800 border ${borderColor} rounded-lg overflow-hidden`}>
                        <div
                          className="p-4 cursor-pointer hover:bg-gray-750 transition-colors"
                          onClick={() => setExpandedInboxId(isExpanded ? null : msg.id)}
                        >
                          <div className="flex items-start gap-3">
                            {!msg.seen && (
                              <span className="mt-1 h-2 w-2 rounded-full bg-blue-400 shrink-0" title="Unread" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-sm text-gray-200 font-medium truncate">
                                  {msg.from?.name || msg.from?.address || 'Unknown'}
                                </span>
                                <span className="text-xs text-gray-500 truncate">
                                  &lt;{msg.from?.address || '?'}&gt;
                                </span>
                                {categoryBadge && (
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${categoryBadge.cls}`}>
                                    {categoryBadge.label}
                                  </span>
                                )}
                                {msg.linkedLeadId && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-900/40 text-purple-400 border border-purple-700/50">
                                    Lead Match
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-300 truncate">{msg.subject || '(no subject)'}</p>
                              {!isExpanded && msg.bodyText && (
                                <p className="text-xs text-gray-500 mt-1 truncate">{msg.bodyText.slice(0, 120)}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <span className="text-xs text-gray-500">
                                {msgDate ? formatDate(msg.date) : '—'}
                              </span>
                              {msg.nextAction && msg.nextAction !== 'none' && (
                                <span className="text-[10px] text-amber-400">
                                  {msg.nextAction.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-700 p-4">
                            <div className="flex gap-4 text-xs text-gray-500 mb-3 flex-wrap">
                              <span>From: {msg.from?.name} &lt;{msg.from?.address}&gt;</span>
                              <span>To: {(msg.to || []).map(t => t.address).join(', ')}</span>
                              {msg.aiReasoning && (
                                <span className="text-gray-400 italic">AI: {msg.aiReasoning}</span>
                              )}
                            </div>
                            <div className="whitespace-pre-wrap font-mono text-sm text-gray-300 bg-gray-900 rounded p-3 max-h-96 overflow-y-auto">
                              {msg.bodyText || '(empty)'}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Follow-ups Section */}
            {activeSection === 'followups' && (
              <div className="space-y-3">
                {followUps.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No leads due for follow-up. Follow-ups are queued 3-5 days after initial contact.
                  </p>
                ) : (
                  followUps.map(lead => (
                    <LeadCard key={lead.id} lead={lead} onUpdate={loadData} />
                  ))
                )}
              </div>
            )}
          </>
        )}

        <div className="p-4 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
          <p className="text-yellow-400 text-xs">
            Approved emails can be sent via SMTP. Requires SMTP secrets configured in Firebase Functions. Emails sent as plain text from christopher@indiechess.com.
          </p>
        </div>
      </div>
    </AdminAuth>
  );
};

export default OutreachPage;
